import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useTranslation } from 'react-i18next';
import { StatsPageInfo, StatCard } from '~/components/Shared/StatsPageInfo/StatsPageInfo';
import { PointsFilterValues } from '~/components/PromotionsBoard/PointsBoard/PointsFilters/PointsFilters';

interface PointsStatistics {
  total_points: number | null;
  participants: number | null;
  completed?: number | null;
  eligible?: number | null;
}

interface Props {
  promoLoyaltyId?: number;
  filters?: PointsFilterValues;
}

const PointsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v6l4 2"/>
  </svg>
);

const ParticipantsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const EligibleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const CompletedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

export const PointsPageInfo = ({ promoLoyaltyId, filters }: Props) => {
  const { t } = useTranslation();

  const { data: stats } = useQuery<PointsStatistics>({
    queryFn: async () => {
      const response = await instance.get('admin/loyalty-history-statistics', {
        params: {
          ...(promoLoyaltyId ? { promo_loyalty_id: promoLoyaltyId } : {}),
          ...(filters?.dates?.length ? { dates: filters.dates } : {}),
          ...(filters?.companies ? { 'filters[companies][names]': filters.companies.names } : {}),
          ...(filters?.accommodation_ids?.length
            ? { 'filters[accommodation_ids]': filters.accommodation_ids }
            : {}),
          ...(filters?.promo_loyalty_ids?.length
            ? { 'filters[promo_loyalty_ids]': filters.promo_loyalty_ids }
            : {}),
        },
      });
      return response.data;
    },
    queryKey: ['loyalty-history-statistics', promoLoyaltyId ?? null, filters ?? null],
  });

  const cards: StatCard[] = [
    {
      value: '__points__',
      label: t('accrued_points'),
      count: stats?.total_points ?? null,
      color: 'purple',
      icon: <PointsIcon />,
      disabled: true,
    },
    {
      value: '__participants__',
      label: t('points_participants'),
      count: stats?.participants ?? null,
      color: 'blue',
      icon: <ParticipantsIcon />,
      disabled: true,
    },
    ...(promoLoyaltyId
      ? [
          {
            value: '__eligible__',
            label: t('points_eligible'),
            count: stats?.eligible ?? null,
            color: 'orange' as const,
            icon: <EligibleIcon />,
            disabled: true,
          },
          {
            value: '__completed__',
            label: t('points_completed'),
            count: stats?.completed ?? null,
            color: 'green' as const,
            icon: <CompletedIcon />,
            disabled: true,
          },
        ]
      : []),
  ];

  return (
    <StatsPageInfo
      activeStatus=""
      onStatusChange={() => {}}
      cards={cards}
    />
  );
};
